import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Menu, Text } from '@mantine/core';
import { useUndo } from './undo';

interface Spot {
  x: number;
  y: number;
  /** Text selected when the menu opened. */
  selection: string;
  /** The link under the pointer, if any. */
  href: string | null;
}

/**
 * The right-click menu wherever a page has no menu of its own (the way
 * Google Docs or Notion do it): undo, copy what is selected, the link under
 * the pointer, reload. Elements with their own menu keep it - they cancel
 * the event first -, text fields keep the browser's menu (paste, spelling),
 * and Shift + right-click always brings up the browser's menu.
 */
export function FallbackContextMenu(): JSX.Element | null {
  const [spot, setSpot] = useState<Spot | null>(null);
  const { undo, last, busy } = useUndo();

  useEffect(() => {
    const onContext = (event: MouseEvent): void => {
      if (event.defaultPrevented || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"], .mantine-Menu-dropdown')) return;
      event.preventDefault();
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      setSpot({ x: event.clientX, y: event.clientY, selection: window.getSelection()?.toString() ?? '', href: anchor?.href ?? null });
    };
    document.addEventListener('contextmenu', onContext);
    return () => document.removeEventListener('contextmenu', onContext);
  }, []);

  if (!spot) return null;
  const close = (): void => setSpot(null);
  const run = (f: () => void) => () => { close(); f(); };
  const copy = (text: string): void => { void navigator.clipboard?.writeText(text).catch(() => undefined); };

  return (
    <Menu opened onChange={(opened) => !opened && close()} position="bottom-start" offset={2} withinPortal shadow="md" width={250}>
      <Menu.Target>
        <div style={{ position: 'fixed', left: spot.x, top: spot.y, width: 1, height: 1 }} />
      </Menu.Target>
      <Menu.Dropdown onContextMenu={(event) => event.preventDefault()}>
        <Menu.Item disabled={!last || busy} onClick={run(() => void undo())}>
          {last ? `Rückgängig: ${last.label.length > 32 ? `${last.label.slice(0, 32)} …` : last.label}` : 'Nichts rückgängig zu machen'}
        </Menu.Item>
        {spot.selection.trim() && <Menu.Item onClick={run(() => copy(spot.selection))}>Markierten Text kopieren</Menu.Item>}
        {spot.href && (
          <>
            <Menu.Divider />
            <Menu.Item onClick={run(() => window.open(spot.href!, '_blank', 'noopener'))}>Link in neuem Tab öffnen</Menu.Item>
            <Menu.Item onClick={run(() => copy(spot.href!))}>Link kopieren</Menu.Item>
          </>
        )}
        <Menu.Divider />
        <Menu.Item onClick={run(() => copy(window.location.href))}>Adresse dieser Seite kopieren</Menu.Item>
        <Menu.Item onClick={run(() => window.location.reload())}>Seite neu laden</Menu.Item>
        <Menu.Divider />
        <Text size="xs" c="dimmed" px="sm" py={4}>Umschalt + Rechtsklick: Menü des Browsers</Text>
      </Menu.Dropdown>
    </Menu>
  );
}
